import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  Layout,
  Menu,
  Button,
  Input,
  message,
  Modal,
  Select,
  Form,
  Upload,
  Typography,
  Drawer,
  Space,
  Row,
  Col,
  InputNumber,
  Switch,
  Tag,
  List,
  Badge,
  Dropdown,
  Checkbox
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  AppstoreAddOutlined,
  DragOutlined,
  MoreOutlined,
  SettingOutlined,
  SortAscendingOutlined
} from '@ant-design/icons'
import { BiSolidCategory } from 'react-icons/bi'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PHOTO_URL } from '../utils/apiutils'
import { PREDEFINED_TAGS } from '../utils/utils'
import { useAuth } from '../hooks/AuthContext'
import { categories as categoryList } from '../utils/utils'
import { reorderProducts } from '../api/store'
import api from '../utils/api'
import {
  normalizeProductStylistIds,
  resolveStylistTagLabel,
  stylistsToSelectOptions
} from '../utils/productStylists'
import imageCompression from 'browser-image-compression'

const { Option } = Select
const { Title, Text } = Typography
const { Header, Sider, Content } = Layout

const PageLayout = styled(Layout)`
  min-height: 100vh;
  @media (max-width: 820px) {
    display: flex;
    flex-direction: column !important;
    .ant-layout-content {
      padding: 10px 0;
      background: #fff;
    }
  }
`

const StyledSider = styled(Sider)`
  display: none;
`

const StyledContent = styled(Content)`
  width: 100% !important;
  padding: 24px;
  @media (max-width: 820px) {
    padding: 16px;
  }
`

const MobileHeader = styled(Header)`
  display: none;
  @media (max-width: 820px) {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    position: sticky;
    top: 0;
    z-index: 1000;
  }
`

const DesktopActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  @media (max-width: 820px) {
    display: none;
  }
`

const MobileActions = styled.div`
  display: none;
  @media (max-width: 820px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  }
`

// 現代化產品列表容器
const ProductListContainer = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  min-height: 400px;
`

// 拖拽排序容器
const DraggableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// 現代化產品卡片
const ModernProductCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #d9d9d9;
  }

  &.dragging {
    transform: rotate(2deg);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 820px) {
    border-radius: 12px;
  }
`

// 產品卡片內容
const ProductCardContent = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 16px;

  @media (max-width: 820px) {
    padding: 12px;
    gap: 12px;
  }
`

// 產品圖片容器
const ProductImageContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  background: #f5f5f5;

  @media (max-width: 820px) {
    width: 60px;
    height: 60px;
    border-radius: 8px;
  }
`

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`

// 產品信息區域
const ProductInfoArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const ProductTitle = styled(Title)`
  margin: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1f2937 !important;
  line-height: 1.4 !important;

  @media (max-width: 820px) {
    font-size: 14px !important;
  }
`

const ProductDescription = styled(Text)`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

// 產品元數據
const ProductMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 820px) {
    gap: 8px;
  }
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
`

const PriceTag = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`

const DurationTag = styled.div`
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`

// 狀態指示器（覆蓋在服務圖片右下角，不遮擋主要內容）
const StatusIndicator = styled.div`
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: ${(props) => (props.active ? '#10b981' : '#f59e0b')};
`

// 操作按鈕區域
const ActionArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f8f9fa;
  color: #6b7280;
  cursor: grab;
  transition: all 0.2s ease;

  &:hover {
    background: #e9ecef;
    color: #495057;
  }

  &:active {
    cursor: grabbing;
  }
`

// 排序模式切換
const SortModeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e9ecef;
  }

  &.active {
    background: #667eea;
    color: white;
    border-color: #667eea;
  }
`

// 類別標籤頁容器
const CategoryTabsContainer = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;

  /* 手機版改用 Drawer 顯示分類，隱藏上方分類區塊，避免重複佔位 */
  @media (max-width: 820px) {
    display: none;
  }
`

// 類別標籤頁
const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  @media (max-width: 820px) {
    gap: 6px;
  }
`

// 類別標籤按鈕
const CategoryTab = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${(props) => (props.active ? '#667eea' : '#f8f9fa')};
  color: ${(props) => (props.active ? '#ffffff' : '#6b7280')};
  border: 1px solid ${(props) => (props.active ? '#667eea' : '#e9ecef')};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.active ? '#5a67d8' : '#e9ecef')};
    transform: translateY(-1px);
  }

  .edit-icon {
    display: none;
    margin-left: 6px;
  }

  &:hover .edit-icon {
    display: inline-flex;
  }

  @media (max-width: 820px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`

// 新增類別按鈕
const AddCategoryButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  background: #f8f9fa;
  color: #6b7280;
  border: 1px dashed #d1d5db;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background: #e9ecef;
    border-color: #9ca3af;
    transform: translateY(-1px);
  }

  @media (max-width: 820px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
`

const ProductPage = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentProduct, setCurrentProduct] = useState(null)
  const [currentCategory, setCurrentCategory] = useState(null)
  const [categories, setCategories] = useState([])
  const [form] = Form.useForm()
  const [categoryForm] = Form.useForm()
  const [fileList, setFileList] = useState([])
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [stylists, setStylists] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user } = useAuth()
  const businessId = user.businessId

  // 避免在 React StrictMode 下重複觸發初始化請求
  const initialDataLoadedRef = useRef(false)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [additionalServiceDrawerOpen, setAdditionalServiceDrawerOpen] =
    useState(false)
  const [currentParentProduct, setCurrentParentProduct] = useState(null)

  // 排序相關狀態
  const [isSortMode, setIsSortMode] = useState(false)
  const [selectedProductIds, setSelectedProductIds] = useState([])
  const [isReordering, setIsReordering] = useState(false)
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false)

  useEffect(() => {
    if (!businessId || initialDataLoadedRef.current) return
    initialDataLoadedRef.current = true

    api
      .get(`/api/business_B/${businessId}/products`)
      .then((response) => {
        setProducts(response.data)
        setFilteredProducts(response.data)
      })
      .catch((error) => console.error('Error fetching products:', error))

    api
      .get(`/api/business_B/${businessId}/categories`)
      .then((response) => {
        setCategories(response.data)
      })
      .catch((error) => console.error('Error fetching categories:', error))

    api
      .get(`/api/business_B/${businessId}/stylists`)
      .then((response) => {
        setStylists(response.data) // 獲取員工列表並保存
      })
      .catch((error) => console.error('Error fetching stylists:', error))
  }, [businessId])

  useEffect(() => {
    const filtered = products.filter(
      (product) =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!selectedCategory || product.category === selectedCategory)
    )
    setFilteredProducts(filtered)
  }, [searchTerm, products, selectedCategory])

  const handleMenuClick = (product, action) => {
    switch (action) {
      case 'edit':
        navigate(`/products/${businessId}/${product._id}`)
        break
      case 'disable':
        disableProduct(product)
        break
      case 'enable':
        enableProduct(product)
        break
      case 'delete':
        deleteProduct(product)
        break
      default:
        break
    }
  }

  const disableProduct = async (product) => {
    try {
      await api.put(
        `/api/business_B/${businessId}/products/${product._id}/disable`
      )
      message.success('服務已隱藏')
      const updatedProducts = products.map((p) =>
        p._id === product._id ? { ...p, isDisabled: true } : p
      )
      setProducts(updatedProducts)
      setFilteredProducts(updatedProducts)
    } catch (error) {
      message.error('隱藏服務失敗')
    }
  }

  const enableProduct = async (product) => {
    try {
      await api.put(
        `/api/business_B/${businessId}/products/${product._id}/enable`
      )
      message.success('產品已重啟')
      const updatedProducts = products.map((p) =>
        p._id === product._id ? { ...p, isDisabled: false } : p
      )
      setProducts(updatedProducts)
      setFilteredProducts(updatedProducts)
    } catch (error) {
      message.error('重啟產品失敗')
    }
  }

  const deleteProduct = async (product) => {
    try {
      await api.delete(`/api/business_B/${businessId}/products/${product._id}`)
      message.success('產品已刪除')
      setProducts(products.filter((p) => p._id !== product._id))
      setFilteredProducts(filteredProducts.filter((p) => p._id !== product._id))
    } catch (error) {
      message.error('刪除產品失敗')
    }
  }

  const hasCategories = categories.length > 0

  const showAddProductModal = () => {
    if (!hasCategories) {
      message.warning('請先添加至少一個服務類別，然後再添加服務。')
      showAddCategoryModal()
      return
    }
    setCurrentProduct(null)
    form.resetFields()
    setFileList([])
    setModalOpen(true)
  }

  const showAddCategoryModal = () => {
    setCurrentCategory(null)
    categoryForm.resetFields()
    setCategoryModalOpen(true)
  }

  const showEditCategoryModal = (category) => {
    setCurrentCategory(category)
    categoryForm.setFieldsValue(category)
    setCategoryModalOpen(true)
  }

  const handleOk = async () => {
    setIsSubmitting(true)
    try {
      const values = await form.validateFields()
      const formData = new FormData()

      // 添加其他字段
      formData.append('name', values.name)
      formData.append(
        'duration',
        values.durationHours * 60 + values.durationMinutes
      )
      formData.append('price', values.price)
      formData.append(
        'stylists',
        JSON.stringify(normalizeProductStylistIds(values.stylists, stylists))
      )
      formData.append('description', values.description || '')
      formData.append('category', values.category)

      // 確保使用正確的文件名
      if (fileList.length > 0) {
        formData.append('image', fileList[0].originFileObj)
      }

      // 打印 FormData 內容
      for (let pair of formData.entries()) {
        console.log('FormData 內容:', pair[0], pair[1])
        if (pair[1] instanceof File) {
          console.log('文件信息:', {
            name: pair[1].name,
            type: pair[1].type,
            size: pair[1].size
          })
        }
      }

      // 確保新服務排在最後：計算當前最大 orderIndex
      // 這裡假設後端支持接收 order 字段
      const maxOrder = products.length > 0 ? products.length : 0
      formData.append('order', maxOrder)

      // 發送請求
      const response = await api.post(
        `/api/business_B/${businessId}/products`,
        formData
      )

      // 上傳成功提示
      message.success('上傳成功！')

      // 關閉模態窗口
      setModalOpen(false)

      // 你可以在這裡添加其他邏輯，比如刷新產品列表
      fetchProducts() // 刷新產品列表
    } catch (error) {
      console.error('提交錯誤:', error)
      // 上傳失敗提示
      message.error('上傳失敗，請稍後再試。')
    } finally {
      setIsSubmitting(false)
    }
  }

  const fetchProducts = () => {
    api
      .get(`/api/business_B/${businessId}/products`)
      .then((response) => {
        setProducts(response.data)
        setFilteredProducts(response.data)
      })
      .catch((error) => console.error('Error fetching products:', error))
  }

  const handleCategoryOk = async () => {
    try {
      const values = await categoryForm.validateFields()
      const { name, type } = values
      if (currentCategory && currentCategory._id) {
        await api.put(
          `/api/business_B/${businessId}/categories/${currentCategory._id}`,
          { name, type }
        )
        message.success('類別已更新')
      } else {
        await api.post(`/api/business_B/${businessId}/categories`, {
          name,
          type
        })
        message.success('類別已新增')
      }
      setCategoryModalOpen(false)
      fetchCategories()
    } catch (error) {
      message.error('提交失敗，請稍後再試')
    }
  }

  const fetchCategories = () => {
    api
      .get(`/api/business_B/${businessId}/categories`)
      .then((response) => {
        setCategories(response.data)
      })
      .catch((error) => console.error('Error fetching categories:', error))
  }

  const deleteCategory = async (category) => {
    if (!category?._id) return
    Modal.confirm({
      title: '刪除服務分類',
      content: `確定要刪除「${category.name}」嗎？若此分類下仍有服務，請先移動或刪除後再刪除分類。`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(
            `/api/business_B/${businessId}/categories/${category._id}`
          )
          message.success('類別已刪除')
          setCategoryModalOpen(false)
          setCurrentCategory(null)
          categoryForm.resetFields()
          if (selectedCategory === category._id) {
            setSelectedCategory(null)
          }
          fetchCategories()
        } catch (error) {
          const msg =
            error.response?.data?.message || error.response?.data?.error
          message.error(msg || '刪除類別失敗')
        }
      }
    })
  }

  const handleFileChange = async ({ fileList: newFileList }) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    }

    try {
      if (newFileList.length > 0 && newFileList[0].originFileObj) {
        const file = newFileList[0].originFileObj

        // HEIC/HEIF 格式直接上傳，由後端處理
        const isHeicHeif = /\.(heic|heif)$/i.test(file.name)

        if (isHeicHeif) {
          // HEIC 文件直接上傳，不進行前端轉換和壓縮
          setFileList(newFileList.slice(0, 1))
          return
        }

        // 非 HEIC/HEIF 格式，進行壓縮
        const compressedFile = await imageCompression(file, options)
        const newFile = new File([compressedFile], file.name, {
          type: compressedFile.type,
          lastModified: new Date().getTime()
        })
        newFileList[0].originFileObj = newFile

        setFileList(newFileList.slice(0, 1))
      } else {
        setFileList(newFileList)
      }
    } catch (error) {
      console.error('圖片轉換失敗:', error)
      message.error('圖片轉換失敗，請使用其他格式的圖片')
      setFileList([])
    }
  }

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId)
    if (categoryId) {
      const filtered = products.filter(
        (product) => product.category === categoryId
      )
      setFilteredProducts(filtered)
    } else {
      setFilteredProducts(products)
    }
  }

  // 拖拽排序處理函數
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = filteredProducts.findIndex(
        (item) => item._id === active.id
      )
      const newIndex = filteredProducts.findIndex(
        (item) => item._id === over.id
      )

      const newProducts = arrayMove(filteredProducts, oldIndex, newIndex)
      setFilteredProducts(newProducts)

      setIsReordering(true)
      try {
        const productOrders = newProducts.map((product, index) => ({
          productId: product._id,
          orderIndex: index
        }))

        await reorderProducts(businessId, productOrders)
        message.success('排序已更新')
        fetchProducts()
      } catch (error) {
        message.error('排序失敗')
        fetchProducts()
      } finally {
        setIsReordering(false)
      }
    }
  }

  // 簡單排序處理函數（使用按鈕排序）
  const handleMoveUp = async (product, index) => {
    if (index === 0) return

    setIsReordering(true)
    try {
      const newProducts = [...filteredProducts]
      const [movedItem] = newProducts.splice(index, 1)
      newProducts.splice(index - 1, 0, movedItem)

      setFilteredProducts(newProducts)

      const productOrders = newProducts.map((p, i) => ({
        productId: p._id,
        orderIndex: i
      }))

      await reorderProducts(businessId, productOrders)
      message.success('排序已更新')
      fetchProducts()
    } catch (error) {
      message.error('排序失敗')
      fetchProducts()
    } finally {
      setIsReordering(false)
    }
  }

  const handleMoveDown = async (product, index) => {
    if (index === filteredProducts.length - 1) return

    setIsReordering(true)
    try {
      const newProducts = [...filteredProducts]
      const [movedItem] = newProducts.splice(index, 1)
      newProducts.splice(index + 1, 0, movedItem)

      setFilteredProducts(newProducts)

      const productOrders = newProducts.map((p, i) => ({
        productId: p._id,
        orderIndex: i
      }))

      await reorderProducts(businessId, productOrders)
      message.success('排序已更新')
      fetchProducts()
    } catch (error) {
      message.error('排序失敗')
      fetchProducts()
    } finally {
      setIsReordering(false)
    }
  }

  // 批次移動至頂部
  const handleBatchMoveToTop = async () => {
    if (selectedProductIds.length === 0) return

    setIsReordering(true)
    try {
      const selectedSet = new Set(selectedProductIds)
      const selectedItems = filteredProducts.filter((p) =>
        selectedSet.has(p._id)
      )
      const remainingItems = filteredProducts.filter(
        (p) => !selectedSet.has(p._id)
      )

      const newProducts = [...selectedItems, ...remainingItems]
      setFilteredProducts(newProducts)

      const productOrders = newProducts.map((p, i) => ({
        productId: p._id,
        orderIndex: i
      }))

      await reorderProducts(businessId, productOrders)
      message.success(`已將 ${selectedProductIds.length} 個項目移至頂部`)
      setSelectedProductIds([]) // 清空選取
      fetchProducts()
    } catch (error) {
      message.error('批次排序失敗')
    } finally {
      setIsReordering(false)
    }
  }

  // 批次移動至底部
  const handleBatchMoveToBottom = async () => {
    if (selectedProductIds.length === 0) return

    setIsReordering(true)
    try {
      const selectedSet = new Set(selectedProductIds)
      const selectedItems = filteredProducts.filter((p) =>
        selectedSet.has(p._id)
      )
      const remainingItems = filteredProducts.filter(
        (p) => !selectedSet.has(p._id)
      )

      const newProducts = [...remainingItems, ...selectedItems]
      setFilteredProducts(newProducts)

      const productOrders = newProducts.map((p, i) => ({
        productId: p._id,
        orderIndex: i
      }))

      await reorderProducts(businessId, productOrders)
      message.success(`已將 ${selectedProductIds.length} 個項目移至底部`)
      setSelectedProductIds([]) // 清空選取
      fetchProducts()
    } catch (error) {
      message.error('批次排序失敗')
    } finally {
      setIsReordering(false)
    }
  }

  // 批次向上移動
  const handleBatchMoveUp = async () => {
    if (selectedProductIds.length === 0) return

    const selectedSet = new Set(selectedProductIds)
    const newProducts = [...filteredProducts]

    // 找到所有選中項的索引並排序
    const indices = []
    newProducts.forEach((p, i) => {
      if (selectedSet.has(p._id)) indices.push(i)
    })

    if (indices.length === 0 || indices[0] === 0) return // 已經在最頂端或沒選中

    setIsReordering(true)
    try {
      // 整體向上移動：遍歷索引，將每個選中項與其前一個非選中項交換，或者更簡單：
      // 找到插入點：indices[0] - 1
      const insertAt = Math.max(0, indices[0] - 1)
      const selectedItems = newProducts.filter((p) => selectedSet.has(p._id))
      const remainingItems = newProducts.filter((p) => !selectedSet.has(p._id))

      remainingItems.splice(insertAt, 0, ...selectedItems)
      const finalProducts = remainingItems

      setFilteredProducts(finalProducts)
      const productOrders = finalProducts.map((p, i) => ({
        productId: p._id,
        orderIndex: i
      }))

      await reorderProducts(businessId, productOrders)
      message.success(`已將 ${selectedProductIds.length} 個項目向上移動`)
      fetchProducts()
    } catch (error) {
      message.error('批次排序失敗')
    } finally {
      setIsReordering(false)
    }
  }

  // 批次向下移動
  const handleBatchMoveDown = async () => {
    if (selectedProductIds.length === 0) return

    const selectedSet = new Set(selectedProductIds)
    const newProducts = [...filteredProducts]

    const indices = []
    newProducts.forEach((p, i) => {
      if (selectedSet.has(p._id)) indices.push(i)
    })

    if (
      indices.length === 0 ||
      indices[indices.length - 1] === newProducts.length - 1
    )
      return

    setIsReordering(true)
    try {
      // 找到插入點：選中項最後一個索引的下一個位置之後
      // 剩餘項中，原先在選中項之後的第一個項的索引是 indices[indices.length-1] + 1
      // 我們要把選中項插在剩餘項序列中的正確位置
      const selectedItems = newProducts.filter((p) => selectedSet.has(p._id))
      const remainingItems = newProducts.filter((p) => !selectedSet.has(p._id))

      // 計算在 remainingItems 中的插入位置
      // 原本 indices[indices.length-1] 是在總數組中的位置，其後有幾個非選中項？
      // 插入點 = indices[0] + 1
      const insertAt = indices[0] + 1

      remainingItems.splice(insertAt, 0, ...selectedItems)
      const finalProducts = remainingItems

      setFilteredProducts(finalProducts)
      const productOrders = finalProducts.map((p, i) => ({
        productId: p._id,
        orderIndex: i
      }))

      await reorderProducts(businessId, productOrders)
      message.success(`已將 ${selectedProductIds.length} 個項目向下移動`)
      fetchProducts()
    } catch (error) {
      message.error('批次排序失敗')
    } finally {
      setIsReordering(false)
    }
  }

  // 切換排序模式
  const toggleSortMode = () => {
    if (isSortMode) {
      setSelectedProductIds([]) // 退出排序模式時清空選取
    }
    setIsSortMode(!isSortMode)
  }

  // 可拖拽的產品卡片組件
  const SortableProductCard = ({ product, index, isSortMode }) => {
    const isSelected = selectedProductIds.includes(product._id)

    const toggleSelection = (e) => {
      e.stopPropagation()
      const newSelected = isSelected
        ? selectedProductIds.filter((id) => id !== product._id)
        : [...selectedProductIds, product._id]
      setSelectedProductIds(newSelected)
    }

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: product._id })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1
    }

    const menuItems = [
      {
        key: 'edit',
        label: '編輯',
        icon: <EditOutlined />,
        onClick: () => handleMenuClick(product, 'edit')
      },
      {
        key: 'toggle',
        label: product.isDisabled ? '啟用' : '禁用',
        icon: product.isDisabled ? <EyeOutlined /> : <EyeInvisibleOutlined />,
        onClick: () =>
          handleMenuClick(product, product.isDisabled ? 'enable' : 'disable')
      },
      {
        type: 'divider'
      },
      {
        key: 'delete',
        label: '刪除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleMenuClick(product, 'delete')
      }
    ]

    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <ModernProductCard>
          <ProductCardContent
            onClick={(e) => {
              if (isSortMode) return
              // 點到任何按鈕、Dropdown 選單項或 Popconfirm 等浮層時，
              // 不要把卡片視為被點擊（避免從「啟用/禁用」誤跳轉到詳情頁）
              if (e.target.closest('button')) return
              if (e.target.closest('.ant-dropdown')) return
              if (e.target.closest('[role="menuitem"]')) return
              if (e.target.closest('.ant-popover')) return
              navigate(`/products/${businessId}/${product._id}`)
            }}
          >
            {/* 拖拽手柄 */}
            {isSortMode && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 8,
                  gap: 12
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={toggleSelection}
                  onClick={(e) => e.stopPropagation()}
                />
                <DragHandle {...listeners}>
                  <DragOutlined />
                </DragHandle>
              </div>
            )}

            {/* 產品圖片 */}
            <ProductImageContainer>
              {product?.image && String(product.image).trim() && (
                <ProductImage
                  src={`${PHOTO_URL}${product.image}`}
                  alt={product.name || '服務圖片'}
                  onError={(e) => {
                    // 載入失敗時隱藏圖片，僅顯示灰底
                    e.target.style.display = 'none'
                  }}
                />
              )}
              <StatusIndicator active={!product.isDisabled} />
            </ProductImageContainer>

            {/* 產品信息 */}
            <ProductInfoArea>
              <ProductTitle level={5}>{product.name}</ProductTitle>
              {product.description && (
                <ProductDescription>{product.description}</ProductDescription>
              )}
              <ProductMeta>
                <PriceTag>NT$ {product.price}</PriceTag>
                <DurationTag>
                  <ClockCircleOutlined /> {Math.floor(product.duration / 60)}h{' '}
                  {product.duration % 60}m
                </DurationTag>
                {product.category && (
                  <Tag color='blue' size='small'>
                    {categories.find((c) => c._id === product.category)?.name ||
                      '未分類'}
                  </Tag>
                )}
              </ProductMeta>
            </ProductInfoArea>

            {/* 操作按鈕 */}
            <ActionArea>
              {isSortMode ? (
                <Badge
                  count={index + 1}
                  style={{ backgroundColor: '#667eea' }}
                />
              ) : (
                <Dropdown
                  menu={{ items: menuItems }}
                  trigger={['click']}
                  placement='bottomRight'
                >
                  <Button type='text' icon={<MoreOutlined />} size='small' />
                </Dropdown>
              )}
            </ActionArea>
          </ProductCardContent>
        </ModernProductCard>
      </div>
    )
  }

  // 現代化產品卡片渲染
  const renderModernProductCard = (product, index, isSortMode = false) => {
    const menuItems = [
      {
        key: 'edit',
        label: '編輯',
        icon: <EditOutlined />,
        onClick: () => handleMenuClick(product, 'edit')
      },
      {
        key: 'toggle',
        label: product.isDisabled ? '啟用' : '禁用',
        icon: product.isDisabled ? <EyeOutlined /> : <EyeInvisibleOutlined />,
        onClick: () =>
          handleMenuClick(product, product.isDisabled ? 'enable' : 'disable')
      },
      {
        type: 'divider'
      },
      {
        key: 'delete',
        label: '刪除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleMenuClick(product, 'delete')
      }
    ]

    return (
      <ModernProductCard
        onClick={(e) => {
          if (isSortMode) return
          // 點到任何按鈕、Dropdown 選單項或 Popconfirm 等浮層時，
          // 不要把卡片視為被點擊（避免從「啟用/禁用」誤跳轉到詳情頁）
          if (e.target.closest('button')) return
          if (e.target.closest('.ant-dropdown')) return
          if (e.target.closest('[role="menuitem"]')) return
          if (e.target.closest('.ant-popover')) return
          navigate(`/products/${businessId}/${product._id}`)
        }}
      >
        <ProductCardContent>
          {/* 排序手柄 */}
          {isSortMode && (
            <DragHandle>
              <DragOutlined />
            </DragHandle>
          )}

          {/* 產品圖片 */}
          <ProductImageContainer>
            {product?.image && String(product.image).trim() && (
              <ProductImage
                src={`${PHOTO_URL}${product.image}`}
                alt={product.name || '服務圖片'}
                onError={(e) => {
                  // 載入失敗時隱藏圖片，僅顯示灰底
                  e.target.style.display = 'none'
                }}
              />
            )}
            <StatusIndicator active={!product.isDisabled} />
          </ProductImageContainer>

          {/* 產品信息 */}
          <ProductInfoArea>
            <ProductTitle level={5}>{product.name}</ProductTitle>
            {product.description && (
              <ProductDescription>{product.description}</ProductDescription>
            )}
            <ProductMeta>
              <PriceTag>NT$ {product.price}</PriceTag>
              <DurationTag>
                <ClockCircleOutlined /> {Math.floor(product.duration / 60)}h{' '}
                {product.duration % 60}m
              </DurationTag>
              {product.category && (
                <Tag color='blue' size='small'>
                  {categories.find((c) => c._id === product.category)?.name ||
                    '未分類'}
                </Tag>
              )}
            </ProductMeta>
          </ProductInfoArea>

          {/* 操作按鈕 */}
          <ActionArea>
            {isSortMode ? (
              <Space>
                <Button
                  size='small'
                  icon={<SortAscendingOutlined />}
                  onClick={() => handleMoveUp(product, index)}
                  disabled={index === 0 || isReordering}
                />
                <Button
                  size='small'
                  icon={
                    <SortAscendingOutlined
                      style={{ transform: 'rotate(180deg)' }}
                    />
                  }
                  onClick={() => handleMoveDown(product, index)}
                  disabled={
                    index === filteredProducts.length - 1 || isReordering
                  }
                />
                <Badge
                  count={index + 1}
                  style={{ backgroundColor: '#667eea' }}
                />
              </Space>
            ) : (
              <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement='bottomRight'
              >
                <Button type='text' icon={<MoreOutlined />} size='small' />
              </Dropdown>
            )}
          </ActionArea>
        </ProductCardContent>
      </ModernProductCard>
    )
  }

  // 渲染類別標籤頁
  const renderCategoryTabs = () => (
    <CategoryTabsContainer>
      <CategoryTabs>
        <CategoryTab
          active={!selectedCategory}
          onClick={() => handleCategorySelect(null)}
        >
          全部服務
        </CategoryTab>
        {categories.map((category) => (
          <CategoryTab
            key={category._id}
            active={selectedCategory === category._id}
            onClick={() => handleCategorySelect(category._id)}
          >
            {category.name}
            <EditOutlined
              className='edit-icon'
              onClick={(e) => {
                e.stopPropagation()
                showEditCategoryModal(category)
              }}
              style={{ fontSize: '12px' }}
            />
          </CategoryTab>
        ))}
        <AddCategoryButton onClick={showAddCategoryModal}>
          <PlusOutlined />
          新增類別
        </AddCategoryButton>
      </CategoryTabs>
    </CategoryTabsContainer>
  )

  const AdditionalServiceDrawer = ({
    open,
    onClose,
    product,
    businessId,
    products: allProducts
  }) => {
    const [form] = Form.useForm()
    const [additionalServices, setAdditionalServices] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedService, setSelectedService] = useState(null)

    // 使用父層傳入的 products，並排除當前服務本身
    const products = (allProducts || []).filter(
      (p) => !product?._id || p._id !== product._id
    )

    // 獲取子服務列表
    useEffect(() => {
      if (product?._id) {
        fetchAdditionalServices()
      }
    }, [product])

    const fetchAdditionalServices = async () => {
      try {
        const response = await api.get(
          `/api/business_B/${businessId}/products/${product._id}/additional-services`
        )
        setAdditionalServices(response.data.data || [])
      } catch (error) {
        console.error('Error fetching additional services:', error)
        message.error('獲取子服務列表失敗')
        setAdditionalServices([])
      }
    }

    const handleSubmit = async (values) => {
      setLoading(true)
      try {
        if (selectedService) {
          // 更新現有子服務
          await api.put(
            `/api/business_B/${businessId}/products/${product._id}/additional-services/${selectedService._id}`,
            values
          )
          message.success('子服務更新成功')
        } else {
          // 新增子服務
          await api.post(
            `/api/business_B/${businessId}/products/${product._id}/additional-services`,
            values
          )
          message.success('子服務新增成功')
        }
        form.resetFields()
        setSelectedService(null)
        fetchAdditionalServices()
      } catch (error) {
        message.error(error.response?.data?.error || '操作失敗')
      } finally {
        setLoading(false)
      }
    }

    // 新增一個函數來獲取服務名稱
    const getServiceNames = (serviceIds = [], productsList = []) => {
      return serviceIds
        .map((id) => productsList.find((p) => p._id === id)?.name)
        .filter(Boolean)
        .join('、')
    }

    return (
      <Drawer
        title={`${product?.name || ''} - 子服務管理`}
        width={720}
        open={open}
        onClose={onClose}
        extra={
          <Space>
            <Button
              onClick={() => {
                form.resetFields()
                setSelectedService(null)
              }}
            >
              新增子服務
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form
              form={form}
              layout='vertical'
              onFinish={handleSubmit}
              initialValues={{
                category: '其他',
                isEnabled: true,
                orderIndex: 0
              }}
            >
              <Form.Item
                name='name'
                label='服務名稱'
                rules={[{ required: true, message: '請輸入服務名稱' }]}
              >
                <Input maxLength={50} showCount />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name='price'
                    label='價格'
                    rules={[{ required: true, message: '請輸入價格' }]}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: '100%' }}
                      formatter={(value) => `$ ${value}`}
                      parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='duration'
                    label='服務時長(分鐘)'
                    rules={[{ required: true, message: '請輸入服務時長' }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name='category'
                label='類別'
                rules={[{ required: true, message: '請選擇類別' }]}
              >
                <Select>
                  {['加強', '升級', '搭配', '其他'].map((cat) => (
                    <Option key={cat} value={cat}>
                      {cat}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name='orderIndex'
                label='排序順序'
                tooltip='數字越小越靠前'
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name='parentServices' label='可作為哪些服務的加選項目'>
                <Select
                  mode='multiple'
                  placeholder='選擇父級服務'
                  optionFilterProp='children'
                >
                  {products.map((p) => (
                    <Option key={p._id} value={p._id}>
                      {p.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label='服務限制' required={false}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name={['restrictions', 'incompatibleWith']}
                      label='不可同時選擇的服務'
                      noStyle
                    >
                      <Select
                        mode='multiple'
                        placeholder='選擇不相容服務'
                        optionFilterProp='children'
                      >
                        {products.map((p) => (
                          <Option key={p._id} value={p._id}>
                            {p.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={['restrictions', 'requiresAny']}
                      label='必須搭配的服務(至少一項)'
                      noStyle
                    >
                      <Select
                        mode='multiple'
                        placeholder='必須搭配的服務'
                        optionFilterProp='children'
                      >
                        {products.map((p) => (
                          <Option key={p._id} value={p._id}>
                            {p.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name='isEnabled' valuePropName='checked'>
                    <Switch checkedChildren='啟用' unCheckedChildren='禁用' />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name='description' label='服務描述'>
                <Input.TextArea rows={4} maxLength={200} showCount />
              </Form.Item>

              <Form.Item>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                  block
                >
                  {selectedService ? '更新子服務' : '新增子服務'}
                </Button>
              </Form.Item>
            </Form>
          </Col>

          <Col span={24}>
            <List
              dataSource={additionalServices}
              locale={{ emptyText: '暫無子服務' }}
              renderItem={(service) => (
                <List.Item
                  actions={[
                    <Button
                      type='link'
                      onClick={() => {
                        setSelectedService(service)
                        form.setFieldsValue({
                          ...service,
                          restrictions: {
                            incompatibleWith:
                              service.restrictions?.incompatibleWith || [],
                            requiresAny: service.restrictions?.requiresAny || []
                          }
                        })
                      }}
                    >
                      編輯
                    </Button>,
                    <Button
                      type='link'
                      danger
                      onClick={async () => {
                        try {
                          await api.delete(
                            `/api/business_B/${businessId}/products/${product._id}/additional-services/${service._id}`
                          )
                          message.success('子服務刪除成功')
                          fetchAdditionalServices()
                        } catch (error) {
                          message.error('刪除失敗')
                        }
                      }}
                    >
                      刪除
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={service.name}
                    description={
                      <Space direction='vertical' size={4}>
                        <Text type='secondary'>{service.description}</Text>
                        <Space wrap>
                          <Tag color='blue'>{service.category}</Tag>
                          <Tag color='green'>${service.price}</Tag>
                          <Tag color='orange'>{service.duration}分鐘</Tag>
                          {!service.isEnabled && <Tag color='red'>已禁用</Tag>}
                        </Space>
                        {service.restrictions?.incompatibleWith?.length > 0 && (
                          <Text type='warning'>
                            ⚠️ 不可與以下服務同時預約：
                            {getServiceNames(
                              service.restrictions.incompatibleWith,
                              products
                            )}
                          </Text>
                        )}
                        {service.restrictions?.requiresAny?.length > 0 && (
                          <Text type='warning'>
                            ⚠️ 需要搭配以下任一服務：
                            {getServiceNames(
                              service.restrictions.requiresAny,
                              products
                            )}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Drawer>
    )
  }

  return (
    <PageLayout>
      <MobileHeader>
        <Button
          icon={<BiSolidCategory />}
          onClick={() => setDrawerOpen(true)}
        />
        <Title level={4} style={{ margin: 0 }}>
          服務管理
        </Title>
        <Space>
          <Button
            onClick={() =>
              navigate(`/products/${businessId}/additional-services`)
            }
            icon={<AppstoreAddOutlined />}
          >
            加選服務
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={showAddProductModal}
            type='primary'
          />
        </Space>
      </MobileHeader>
      <StyledContent>
        {/* 類別標籤頁 */}
        {renderCategoryTabs()}
        <DesktopActions>
          <Space>
            <Input.Search
              placeholder='搜索服務'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '300px' }}
              prefix={<SearchOutlined />}
            />
            <SortModeToggle
              className={isSortMode ? 'active' : ''}
              onClick={toggleSortMode}
            >
              <SortAscendingOutlined />
              {isSortMode ? '完成排序' : '調整順序'}
            </SortModeToggle>

            {isSortMode && selectedProductIds.length > 0 && (
              <Space>
                <Button
                  onClick={handleBatchMoveToTop}
                  icon={<SortAscendingOutlined />}
                  type='primary'
                  ghost
                >
                  移至頂部
                </Button>
                <Button
                  onClick={handleBatchMoveUp}
                  icon={
                    <PlusOutlined
                      style={{ transform: 'rotate(45deg)', fontSize: 12 }}
                    />
                  }
                  type='default'
                >
                  向上移動
                </Button>
                <Button
                  onClick={handleBatchMoveDown}
                  icon={
                    <PlusOutlined
                      style={{ transform: 'rotate(225deg)', fontSize: 12 }}
                    />
                  }
                  type='default'
                >
                  向下移動
                </Button>
                <Button
                  onClick={handleBatchMoveToBottom}
                  icon={
                    <SortAscendingOutlined
                      style={{ transform: 'rotate(180deg)' }}
                    />
                  }
                  type='primary'
                  ghost
                >
                  移至底部
                </Button>
                <Text type='secondary' style={{ marginLeft: 8 }}>
                  已選 {selectedProductIds.length} 項
                </Text>
                <Button
                  onClick={() => setSelectedProductIds([])}
                  type='text'
                  danger
                >
                  取消
                </Button>
              </Space>
            )}

            <Button
              onClick={() =>
                navigate(`/products/${businessId}/additional-services`)
              }
              icon={<AppstoreAddOutlined />}
            >
              加選服務管理
            </Button>
          </Space>
          <Space>
            {isSortMode && (
              <Button onClick={toggleSortMode} icon={<SettingOutlined />}>
                退出排序
              </Button>
            )}
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={showAddProductModal}
            >
              新增服務
            </Button>
          </Space>
        </DesktopActions>
        <MobileActions>
          <Input.Search
            placeholder='搜索服務'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <SortModeToggle
            className={isSortMode ? 'active' : ''}
            onClick={toggleSortMode}
          >
            <SortAscendingOutlined />
            {isSortMode ? '完成排序' : '調整順序'}
          </SortModeToggle>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={showAddProductModal}
          >
            新增服務
          </Button>
        </MobileActions>

        {isSortMode && selectedProductIds.length > 0 && (
          <div
            style={{
              padding: '8px 16px',
              background: '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: 16,
              borderRadius: 8
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Text strong>已選 {selectedProductIds.length} 項</Text>
              <Button
                size='small'
                type='text'
                danger
                onClick={() => setSelectedProductIds([])}
              >
                取消
              </Button>
            </div>
            <Space wrap size='small'>
              <Button size='small' onClick={handleBatchMoveToTop}>
                頂部
              </Button>
              <Button size='small' onClick={handleBatchMoveUp}>
                向上
              </Button>
              <Button size='small' onClick={handleBatchMoveDown}>
                向下
              </Button>
              <Button size='small' onClick={handleBatchMoveToBottom}>
                底部
              </Button>
            </Space>
          </div>
        )}
        {hasCategories ? (
          <ProductListContainer>
            {isSortMode ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredProducts.map((p) => p._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DraggableList>
                    {filteredProducts.map((product, index) => (
                      <SortableProductCard
                        key={product._id}
                        product={product}
                        index={index}
                        isSortMode={isSortMode}
                      />
                    ))}
                  </DraggableList>
                </SortableContext>
              </DndContext>
            ) : (
              <DraggableList>
                {filteredProducts.map((product, index) => (
                  <div key={product._id}>
                    {renderModernProductCard(product, index, isSortMode)}
                  </div>
                ))}
              </DraggableList>
            )}
            {filteredProducts.length === 0 && (
              <EmptyState>
                <Title level={4}>暫無服務</Title>
                <Text>請添加您的第一個服務項目</Text>
                <br />
                <br />
                <Button type='primary' onClick={showAddProductModal}>
                  新增服務
                </Button>
              </EmptyState>
            )}
          </ProductListContainer>
        ) : (
          <EmptyState>
            <Title level={4}>還沒有服務類別</Title>
            <Text>請先添加服務類別，然後再添加服務。</Text>
            <br />
            <br />
            <Button type='primary' onClick={showAddCategoryModal}>
              添加服務類別
            </Button>
          </EmptyState>
        )}
      </StyledContent>

      <Drawer
        title='服務類別'
        placement='left'
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={300}
      >
        <div style={{ padding: '16px 0' }}>
          <CategoryTabs>
            <CategoryTab
              active={!selectedCategory}
              onClick={() => {
                handleCategorySelect(null)
                setDrawerOpen(false)
              }}
            >
              全部服務
            </CategoryTab>
            {categories.map((category) => (
              <CategoryTab
                key={category._id}
                active={selectedCategory === category._id}
                onClick={() => {
                  handleCategorySelect(category._id)
                  setDrawerOpen(false)
                }}
              >
                {category.name}
                <EditOutlined
                  className='edit-icon'
                  onClick={(e) => {
                    e.stopPropagation()
                    showEditCategoryModal(category)
                  }}
                  style={{ fontSize: '12px' }}
                />
              </CategoryTab>
            ))}
            <AddCategoryButton onClick={showAddCategoryModal}>
              <PlusOutlined />
              新增類別
            </AddCategoryButton>
          </CategoryTabs>
        </div>
      </Drawer>

      <Drawer
        title={currentProduct ? '編輯服務' : '新增服務'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        width={window.innerWidth < 820 ? '100%' : 480}
        bodyStyle={{ padding: '20px' }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px'
            }}
          >
            <Button
              style={{ flex: 1, marginRight: '8px' }}
              onClick={() => setModalOpen(false)}
              disabled={isSubmitting}
            >
              取消
            </Button>
            <Button
              type='primary'
              style={{ flex: 1 }}
              onClick={handleOk}
              loading={isSubmitting}
            >
              確定
            </Button>
          </div>
        }
      >
        {hasCategories ? (
          <Form form={form} layout='vertical'>
            <Form.Item
              name='name'
              label='服務名稱'
              rules={[{ required: true, message: '請輸入服務名稱' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name='price'
              label='價格（臺幣）'
              rules={[{ required: true, message: '請輸入價格' }]}
            >
              <Input type='number' />
            </Form.Item>
            <Form.Item
              name='durationHours'
              label='進行時間（小時）'
              rules={[{ required: true, message: '請選擇進行時間（小時）' }]}
            >
              <Select>
                {[...Array(9)].map((_, i) => (
                  <Option key={i} value={i}>
                    {i}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='durationMinutes'
              label='進行時間（分鐘）'
              rules={[{ required: true, message: '請選擇進行時間（分鐘）' }]}
            >
              <Select>
                {[0, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((minute) => (
                  <Option key={minute} value={minute}>
                    {minute}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name='tags' label='標籤'>
              <Select mode='multiple'>
                {PREDEFINED_TAGS.map((tag) => (
                  <Option key={tag} value={tag}>
                    {tag}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='category'
              label='服務分類'
              rules={[{ required: true, message: '請選擇服務分類' }]}
            >
              <Select>
                {categories.map((category) => (
                  <Option key={category._id} value={category._id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='stylists'
              label='可服務人員'
              rules={[{ required: true, message: '請選擇可服務人員' }]}
              normalize={(v) =>
                stylists.length
                  ? normalizeProductStylistIds(
                      Array.isArray(v) ? v : v == null ? [] : [v],
                      stylists
                    )
                  : Array.isArray(v)
                    ? v
                    : v == null
                      ? []
                      : [v]
              }
            >
              <Select
                mode='multiple'
                optionFilterProp='label'
                options={stylistsToSelectOptions(stylists)}
                tagRender={(props) => {
                  const { value: v, closable, onClose } = props
                  return (
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      style={{ marginInlineEnd: 4 }}
                    >
                      {resolveStylistTagLabel(v, stylists)}
                    </Tag>
                  )
                }}
              />
            </Form.Item>
            <Form.Item name='description' label='服務描述'>
              <Input.TextArea />
            </Form.Item>
            <Form.Item name='image' label='服務照片（可選）'>
              <Upload
                listType='picture-card'
                fileList={fileList}
                onChange={handleFileChange}
                beforeUpload={(file) => {
                  // 檢查文件類型
                  const isValidType =
                    file.type === 'image/jpeg' ||
                    file.type === 'image/jpg' ||
                    file.type === 'image/png' ||
                    file.type === 'image/gif' ||
                    /\.(heic|heif)$/i.test(file.name)

                  if (!isValidType) {
                    message.error('只支持 JPG、PNG、GIF 或 HEIC 格式的圖片')
                    return Upload.LIST_IGNORE
                  }

                  // 檢查文件大小（50MB 限制）
                  if (file.size > 50 * 1024 * 1024) {
                    message.error('圖片大小不能超過 50MB')
                    return Upload.LIST_IGNORE
                  }

                  return false // 阻止自動上傳，手動處理
                }}
                accept='image/jpeg,image/jpg,image/png,image/gif,image/heic,image/heif'
                multiple={false}
              >
                {fileList.length < 1 && (
                  <div>
                    <PlusOutlined />
                    <div>上傳照片</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Form>
        ) : (
          <EmptyState>
            <Text>請先添加至少一個服務類別，然後再添加服務。</Text>
            <br />
            <br />
            <Button type='primary' onClick={showAddCategoryModal}>
              添加服務類別
            </Button>
          </EmptyState>
        )}
      </Drawer>

      <Modal
        title={currentCategory ? '編輯類別' : '新增類別'}
        open={categoryModalOpen}
        onOk={handleCategoryOk}
        onCancel={() => {
          setCategoryModalOpen(false)
          setCurrentCategory(null)
          categoryForm.resetFields()
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              {currentCategory ? (
                <Button
                  style={{ backgroundColor: '#ff4d4f', color: '#fff' }}
                  onClick={() => deleteCategory(currentCategory)}
                >
                  刪除
                </Button>
              ) : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setCategoryModalOpen(false)
                  setCurrentCategory(null)
                  categoryForm.resetFields()
                }}
              >
                取消
              </Button>
              <Button
                style={{ marginLeft: '10px' }}
                type='primary'
                onClick={handleCategoryOk}
              >
                確定
              </Button>
            </div>
          </div>
        }
      >
        <Form form={categoryForm} layout='vertical'>
          <Form.Item
            name='name'
            label='類別名稱'
            rules={[{ required: true, message: '請輸入類別名稱' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name='type'
            label='服務類型'
            rules={[{ required: true, message: '請選擇服務類型' }]}
            extra='您可以創建自己的分類，但需要選擇一個預定義的服務類型。'
          >
            <Select>
              {categoryList
                .filter((category) => category.type !== 'all')
                .map((category) => (
                  <Option key={category.type} value={category.type}>
                    {category.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <AdditionalServiceDrawer
        open={additionalServiceDrawerOpen}
        onClose={() => {
          setAdditionalServiceDrawerOpen(false)
          setCurrentParentProduct(null)
        }}
        product={currentParentProduct}
        businessId={businessId}
        products={products}
      />
    </PageLayout>
  )
}

export default ProductPage
